import * as styles from "./OpportunitiesApplication.module.css";

import {
  ButtonElement,
  Checkbox,
  FileInput,
  Input,
  Select,
} from "@components/ui";
import React, { useEffect, useRef, useState } from "react";
import { RecaptchaSkeleton, RenderOnViewportEntry } from "@components/general";
import { useApplicationForm, useSiteMetadata } from "@hooks";

import { BackendMethods } from "@classes";
import { HtmlOutput } from "@components/html";
import ReCAPTCHA from "react-google-recaptcha";
import { allOpportunityTypes } from "@store";
import { errorMessages } from "@models";
import { navigate } from "gatsby";

/**
 * This component renders the application form for engineers, partners and head office opportunitites.
 * It contains fields for the user's name, email, phone, years of experience, address, trades they cover, CV and a recaptcha.
 * When the user submits the form, it validates the form data, submits the form to the backend and handles the response.
 * If the form is invalid, it sets the form error state to true and logs the error.
 * If the form is valid, it submits the form to the backend and logs the form fill event if it's successful, otherwise it logs the error.
 *
 * @function OpportunitiesApplication
 * @param {Object} props - The component's props.
 * @param {string} props.url - The URL of the form.
 * @param {"head-office" | "engineer" | "partner"} props.type - The type of form to render.
 * @param {string} [props.className] - The class name for the component.
 * @returns {JSX.Element} - The component.
 */
const OpportunitiesApplication = ({
  url,
  type,
  className,
}: {
  url: string;
  type: "head-office" | "engineer" | "partner";
  className?: string;
}) => {
  // The application form context
  const { state: applicationForm, dispatch } = useApplicationForm();

  // The site phone number
  const { phoneNumber } = useSiteMetadata();

  // Establish a backend methods instance
  const backendMethods = new BackendMethods();

  // Refs for the hidden fields
  const hdnSiteRef = useRef<HTMLInputElement>(null);
  const hdnDateRef = useRef<HTMLInputElement>(null);

  // State for the form checking if it's incomplete, there's an error, what the recaptcha token is and if there's a file
  const [incompleteForm, setIncompleteForm] = useState(false); //When a user clicks the submit button before filling in all the fields
  const [formError, setFormError] = useState("");

  // Use effect to update fields we are confident about
  // Has to be done in useEffect otherwise it causes a conflict with the ApplicationFormProvider
  useEffect(() => {
    // Reset the form so that if people apply for another job during the same session, it doesn't use the old data
    dispatch({ type: "RESET_FORM" });
    // Default payload for fields we know are valid
    const payload = {
      valid: true,
      type: allOpportunityTypes,
    };
    // Set the website field as we know this is valid
    dispatch({
      type: "SET_VALUE",
      payload: {
        key: "website",
        value: { ...payload, value: url },
      },
    });
    // Set the type field as we know this is valid
    dispatch({
      type: "SET_VALUE",
      payload: {
        key: "hdnType",
        value: { ...payload, value: type },
      },
    });
  }, []);

  // Set the state when the user interacts with the captcha form
  const onRecaptchaChange = (token: string | null) => {
    // Update the context with the recaptcha token
    // Setting valid to true as there is a separate check for this in the BackendMethods class.
    dispatch({
      type: "SET_VALUE",
      payload: {
        key: "recaptchaToken",
        value: { value: token || "", valid: true },
      },
    });
  };

  /**
   * Handles the submission of the form by validating the form data, submitting the form to the backend and handling the response.
   * If the form is invalid, it sets the form error state to true and logs the error.
   * If the form is valid, it submits the form to the backend and logs the form fill event if it's successful, otherwise it logs the error.
   * @function onClickHandler
   * @async
   * @returns {Promise<void>}
   */
  const onClickHandler = async () => {
    // Reset the form error
    setFormError("");

    // Get the values from the hidden fields
    const hdnSiteValue = (hdnSiteRef.current as HTMLInputElement).value;
    const hdnDateValue = (hdnDateRef.current as HTMLInputElement).value;

    // Dispatching to the context at this point means when we pass the form
    // object to the back end, we don't have the latest version. An onchange event isn't
    // suitable for the hidden fields so these the current value for these are added manually.
    const params = {
      ...applicationForm,
      hdnSite: { ...applicationForm.hdnSite, value: hdnSiteValue },
      hdnDate: { ...applicationForm.hdnDate, value: hdnDateValue },
    };

    try {
      // Attempt to submit the form
      await backendMethods.callBackend({
        controller: "Recruitment",
        endpoint: "SetRecruitment",
        method: "POST",
        params,
        subFormType: type,
      });

      // On success, navigate to the thank you page
      navigate("/careers/opportunities/thank-you");
    } catch (error: any) {
      // If there's no paragraph tag in the error message, add one
      const errorMessage = !error.message.match(/^<p>.*?/i)
        ? `<p>${error.message}</p>`
        : error.message;

      // If the form is invalid, errors will appear next to the invalid fields so we don't
      // need to set the form error state for invalid fields.
      if (errorMessage.includes(errorMessages.invalidFields))
        setIncompleteForm(true);
      else if (backendMethods.isCommonError(errorMessage)) {
        // If it's a common error, just display the message.
        setFormError(errorMessage);
      } else {
        // These are unexpected errors. Log them in the console.
        console.error(error);

        // If the error contains Axios, it's likely a network error, so display a user friendly message
        const userFriendlyMessage = `<p>An error occurred when attempting to submit your request. Please call 0121 210 6536 or try again later.</p>`;
        setFormError(
          errorMessage.includes("Axios") ? userFriendlyMessage : errorMessage
        );
      }
    }
  };

  return (
    <section id="opportunitiesApplication" className={className}>
      <form className={styles.oaForm} onSubmit={onClickHandler}>
        {/* Fields used on all application forms. */}

        {/* Forename and Surname */}
        <div className={styles.oaForm50}>
          <Input
            type="text"
            title="Forename"
            name="txtForename"
            placeholder="John"
            dispatch={dispatch}
            formType="ApplicationForm"
            incompleteForm={incompleteForm}
          />
        </div>
        <div className={styles.oaForm50}>
          <Input
            type="text"
            title="Surname"
            name="txtSurname"
            placeholder="Smith"
            dispatch={dispatch}
            formType="ApplicationForm"
            incompleteForm={incompleteForm}
          />
        </div>

        {/* Email and Phone */}
        <div className={styles.oaForm50}>
          <Input
            type="email"
            title="Email address"
            name="txtEmail"
            placeholder="john.smith@emailhost.com"
            dispatch={dispatch}
            formType="ApplicationForm"
            incompleteForm={incompleteForm}
          />
        </div>
        <div className={styles.oaForm50}>
          <Input
            type="tel"
            title="Phone"
            name="txtPhone"
            placeholder={phoneNumber}
            dispatch={dispatch}
            formType="ApplicationForm"
            incompleteForm={incompleteForm}
          />
        </div>

        {/* Years of experience */}
        <div className={styles.oaForm100}>
          <Select
            title="Years of Experience"
            name="selYearsExperience"
            placeholder="Please select"
            dispatch={dispatch}
            formType="ApplicationForm"
            options={[
              { key: "1 year", value: "1 year" },
              { key: "2 years", value: "2 years" },
              { key: "3 years", value: "3 years" },
              { key: "4 years", value: "4 years" },
              { key: "5+ years", value: "5+ years" },
            ]}
            incompleteForm={incompleteForm}
          />
        </div>

        {/* Head office specific fields */}
        {type === "head-office" ? (
          <>
            {/* Cover letter and CV */}
            <div className={styles.oaForm100}>
              <Input
                type="textarea"
                title="Cover Letter"
                name="txtCoverLetter"
                placeholder="I would like to work for Rightio because..."
                dispatch={dispatch}
                formType="ApplicationForm"
                incompleteForm={incompleteForm}
              />
            </div>
            <div className={styles.oaForm100}>
              <FileInput
                title="CV"
                name="fileCV"
                placeholder="Drag and drop a file here or click here to choose a file"
                dispatch={dispatch}
                formType="ApplicationForm"
                incompleteForm={incompleteForm}
                accept={[
                  { ext: ".txt", mime: "text/plain" },
                  { ext: ".rtf", mime: "application/rtf" },
                  { ext: ".pdf", mime: "application/pdf" },
                  { ext: ".doc", mime: "application/msword" },
                  {
                    ext: ".docx",
                    mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                  },
                  {
                    ext: ".odt",
                    mime: "application/vnd.oasis.opendocument.text",
                  },
                ]}
              />
            </div>
          </>
        ) : (
          // Fields specific to partners and engineers
          <>
            {/* Address fields */}
            <div className={styles.oaForm50}>
              <Input
                type="text"
                title="Property"
                name="txtProperty"
                placeholder="210 - 222"
                dispatch={dispatch}
                formType="ApplicationForm"
                incompleteForm={incompleteForm}
              />
            </div>
            <div className={styles.oaForm50}>
              <Input
                type="text"
                title="Street"
                name="txtStreet"
                placeholder="Hagley Road West"
                dispatch={dispatch}
                formType="ApplicationForm"
                incompleteForm={incompleteForm}
              />
            </div>
            <div className={styles.oaForm50}>
              <Input
                type="text"
                title="Town"
                name="txtTown"
                placeholder="Oldbury"
                dispatch={dispatch}
                formType="ApplicationForm"
                incompleteForm={incompleteForm}
              />
            </div>
            <div className={styles.oaForm50}>
              <Input
                type="text"
                title="Postcode"
                name="txtPostcode"
                placeholder="B68 0NP"
                dispatch={dispatch}
                formType="ApplicationForm"
                incompleteForm={incompleteForm}
              />
            </div>

            {/* Trades checkboxes */}
            {/* Plumbing checkbox */}
            <div className={styles.oaForm100}>
              <Checkbox
                name="chkTradesPlumbing"
                value={"true"}
                required={false}
                dispatch={dispatch}
                formType="ApplicationForm"
                incompleteForm={incompleteForm}
              >
                <strong>Plumbing &amp; Drainage</strong>
                <span className={styles.oaFormHighlight}>
                  (City &amp; Guilds, NVQs, IPHE, SNIPEF, WIAPS, APHC etc.)
                </span>
              </Checkbox>
            </div>
            {/* Drainage checkbox */}
            <div className={styles.oaForm100}>
              <Checkbox
                name="chkTradesDrainage"
                value={"true"}
                required={false}
                dispatch={dispatch}
                formType="ApplicationForm"
                incompleteForm={incompleteForm}
              >
                <strong>Drainage</strong>
                <span className={styles.oaFormHighlight}>
                  (Specialist drainage engineer with jetting equipment, CCTV
                  etc.)
                </span>
              </Checkbox>
            </div>
            {/* Heating checkbox */}
            <div className={styles.oaForm100}>
              <Checkbox
                name="chkTradesHeating"
                value={"true"}
                required={false}
                dispatch={dispatch}
                formType="ApplicationForm"
                incompleteForm={incompleteForm}
              >
                <strong>Heating</strong>
                <span className={styles.oaFormHighlight}>
                  (Gas Safe, fault finding, repairs etc.)
                </span>
              </Checkbox>
            </div>
            {/* Gas boiler installation checkbox */}
            <div className={styles.oaForm100}>
              <Checkbox
                name="chkTradesGasBoilerInstall"
                value={"true"}
                required={false}
                dispatch={dispatch}
                formType="ApplicationForm"
                incompleteForm={incompleteForm}
              >
                <strong>Gas Boiler Installation</strong>
                <span className={styles.oaFormHighlight}>(Gas Safe)</span>
              </Checkbox>
            </div>
            {/* Locksmith checkbox */}
            <div className={styles.oaForm100}>
              <Checkbox
                name="chkTradesLocksmith"
                value={"true"}
                required={false}
                dispatch={dispatch}
                formType="ApplicationForm"
                incompleteForm={incompleteForm}
              >
                <strong>Locksmith</strong>
                <span className={styles.oaFormHighlight}>
                  (Master Locksmiths Association)
                </span>
              </Checkbox>
            </div>
            {/* Electrician checkbox */}
            <div className={styles.oaForm100}>
              <Checkbox
                name="chkTradesElectrician"
                value={"true"}
                required={false}
                dispatch={dispatch}
                formType="ApplicationForm"
                incompleteForm={incompleteForm}
              >
                <strong>Electrician</strong>
                <span className={styles.oaFormHighlight}>
                  (NICEIC / NAPIT / SELECT / ECA / ELECSA etc.)
                </span>
              </Checkbox>
            </div>

            {/* CV specific to just engineers */}
            {type === "engineer" && (
              <div className={styles.oaForm100}>
                <FileInput
                  title="CV"
                  name="fileCV"
                  placeholder="Drag and drop a file here or click here to choose a file"
                  dispatch={dispatch}
                  formType="ApplicationForm"
                  incompleteForm={incompleteForm}
                  accept={[
                    { ext: ".txt", mime: "text/plain" },
                    { ext: ".rtf", mime: "application/rtf" },
                    { ext: ".pdf", mime: "application/pdf" },
                    { ext: ".doc", mime: "application/msword" },
                    {
                      ext: ".docx",
                      mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                    },
                    {
                      ext: ".odt",
                      mime: "application/vnd.oasis.opendocument.text",
                    },
                  ]}
                />
              </div>
            )}
          </>
        )}

        {/* Lazy loaded recaptcha component */}
        <div className={styles.oaForm100}>
          <RenderOnViewportEntry
            threshold={0.25}
            fallBackNode={<RecaptchaSkeleton />}
          >
            <ReCAPTCHA
              sitekey={process.env.GATSBY_RECAPTCHA_SITEKEY!}
              onChange={(token) => {
                onRecaptchaChange(token);
              }}
            />
          </RenderOnViewportEntry>
        </div>

        {/* Submit button and hidden fields used to prevent robots */}
        <div className={styles.oaForm100}>
          <input ref={hdnSiteRef} type="hidden" name="hdnSite" value="" />
          <input ref={hdnDateRef} type="hidden" name="hdnDate" value="" />
          <ButtonElement
            onClick={onClickHandler}
            title="Submit"
            showLoading={true}
          />
        </div>

        {/* Error message */}
        {formError.length > 0 && (
          <div className={`${styles.oaForm100} ${styles.oaFormError}`}>
            <HtmlOutput html={formError} />
          </div>
        )}
      </form>
    </section>
  );
};

export default OpportunitiesApplication;
